import React from 'react';
import { Shield, Users, User, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

export default function RoleHierarchyDiagram() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Role Hierarchy & "Assign to Manager" Explained</CardTitle>
        <CardDescription>
          Understanding the team structure and management relationships
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-8">
          {/* Visual Hierarchy */}
          <div className="flex flex-col items-center space-y-4">
            {/* Admin Level */}
            <div className="w-full max-w-md">
              <div className="bg-gradient-to-r from-amber-500 to-rose-500 text-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <Shield className="h-8 w-8" />
                  <div>
                    <h3 className="text-xl font-bold">Admin</h3>
                    <p className="text-sm opacity-90">Full System Access</p>
                  </div>
                </div>
                <ul className="text-sm space-y-1 opacity-90">
                  <li>✓ Manage all users</li>
                  <li>✓ Create Marketing Managers</li>
                  <li>✓ Change any user's role</li>
                  <li>✓ Global settings access</li>
                </ul>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex items-center gap-4">
              <div className="h-8 w-0.5 bg-gray-300"></div>
            </div>

            {/* Marketing Manager Level */}
            <div className="w-full max-w-3xl grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp className="h-8 w-8" />
                  <div>
                    <h3 className="text-xl font-bold">Marketing Manager A</h3>
                    <p className="text-xs opacity-90">Managed by: Admin</p>
                  </div>
                </div>
                <ul className="text-sm space-y-1 opacity-90">
                  <li>✓ Manage team members</li>
                  <li>✓ Assign leads to team</li>
                  <li>✓ View team analytics</li>
                  <li>✗ Cannot access admin panel</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-6 rounded-lg shadow-lg">
                <div className="flex items-center gap-3 mb-3">
                  <TrendingUp className="h-8 w-8" />
                  <div>
                    <h3 className="text-xl font-bold">Marketing Manager B</h3>
                    <p className="text-xs opacity-90">Managed by: Admin</p>
                  </div>
                </div>
                <ul className="text-sm space-y-1 opacity-90">
                  <li>✓ Manage their team only</li>
                  <li>✓ Assign leads to team</li>
                  <li>✓ Track team performance</li>
                  <li>✗ Limited to their team</li>
                </ul>
              </div>
            </div>

            {/* Arrow */}
            <div className="flex items-center gap-8">
              <div className="h-8 w-0.5 bg-gray-300"></div>
              <div className="h-8 w-0.5 bg-gray-300"></div>
            </div>

            {/* Marketing Users Level */}
            <div className="w-full max-w-3xl grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gradient-to-br from-green-400 to-emerald-500 text-white p-4 rounded-lg shadow text-center">
                <User className="h-6 w-6 mx-auto mb-2" />
                <p className="font-semibold text-sm">User 1</p>
                <p className="text-xs opacity-90">→ Manager A</p>
              </div>

              <div className="bg-gradient-to-br from-green-400 to-emerald-500 text-white p-4 rounded-lg shadow text-center">
                <User className="h-6 w-6 mx-auto mb-2" />
                <p className="font-semibold text-sm">User 2</p>
                <p className="text-xs opacity-90">→ Manager A</p>
              </div>

              <div className="bg-gradient-to-br from-violet-400 to-purple-500 text-white p-4 rounded-lg shadow text-center">
                <User className="h-6 w-6 mx-auto mb-2" />
                <p className="font-semibold text-sm">User 3</p>
                <p className="text-xs opacity-90">→ Manager B</p>
              </div>

              <div className="bg-gradient-to-br from-violet-400 to-purple-500 text-white p-4 rounded-lg shadow text-center">
                <User className="h-6 w-6 mx-auto mb-2" />
                <p className="font-semibold text-sm">User 4</p>
                <p className="text-xs opacity-90">→ Manager B</p>
              </div>
            </div>
          </div>

          {/* Explanation Section */}
          <div className="bg-blue-50 dark:bg-blue-950 p-6 rounded-lg border-l-4 border-blue-500">
            <h4 className="font-bold text-lg mb-3 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              What is "Assign to Manager"?
            </h4>
            <div className="space-y-3 text-sm">
              <p>
                When you change a user's role to <strong>Marketing User</strong> or <strong>Marketing Manager</strong>, 
                you can assign them to a supervisor. This creates a management relationship shown by the arrows above.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                  <h5 className="font-semibold mb-2 text-green-600">✓ Required For:</h5>
                  <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                    <li>• <strong>Marketing Users</strong> - MUST have a manager</li>
                    <li>• Creates team structure</li>
                    <li>• Enables lead assignment</li>
                    <li>• Tracks team performance</li>
                  </ul>
                </div>

                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
                  <h5 className="font-semibold mb-2 text-orange-600">⚠ Optional For:</h5>
                  <ul className="space-y-1 text-gray-700 dark:text-gray-300">
                    <li>• <strong>Marketing Managers</strong> - Can have admin as manager</li>
                    <li>• <strong>Admins</strong> - No manager needed</li>
                    <li>• <strong>Regular Users</strong> - No manager needed</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Key Benefits */}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 p-4 rounded-lg">
              <div className="text-2xl mb-2">🎯</div>
              <h5 className="font-semibold mb-1">Team Organization</h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Clear structure with defined teams and managers
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 p-4 rounded-lg">
              <div className="text-2xl mb-2">🔒</div>
              <h5 className="font-semibold mb-1">Access Control</h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Managers see only their team's data
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 p-4 rounded-lg">
              <div className="text-2xl mb-2">📊</div>
              <h5 className="font-semibold mb-1">Performance Tracking</h5>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Monitor team metrics and individual performance
              </p>
            </div>
          </div>

          {/* Example */}
          <div className="bg-amber-50 dark:bg-amber-950 p-6 rounded-lg border-l-4 border-amber-500">
            <h4 className="font-bold text-lg mb-3">💡 Example: Creating a Team</h4>
            <ol className="space-y-2 text-sm">
              <li className="flex gap-2">
                <span className="font-bold text-amber-600">1.</span>
                <span>Admin promotes <strong>"Sarah"</strong> to Marketing Manager (Assign to: None)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-amber-600">2.</span>
                <span>Admin promotes <strong>"John"</strong> to Marketing User (Assign to: <strong>Sarah</strong>)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-amber-600">3.</span>
                <span>Admin promotes <strong>"Mike"</strong> to Marketing User (Assign to: <strong>Sarah</strong>)</span>
              </li>
              <li className="flex gap-2">
                <span className="font-bold text-amber-600">Result:</span>
                <span>Sarah now manages a team of John and Mike. She can assign leads to them and track their performance.</span>
              </li>
            </ol>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
